import { z } from "zod";
import { JobAlertExperienceSchema } from "./jobs";
import { COURSE_ENQUIRY_CONSENT } from "./course-enquiry.constants";

export const CourseSortSchema = z.enum([
  "recommended",
  "shortest",
  "top_rated",
  "recently_updated",
  "trending",
  "best_value",
]);
export type CourseSort = z.infer<typeof CourseSortSchema>;
export const CourseRecommendationRequestSchema = z.object({
  job_id: z.string().min(1).max(512),
  creator_handle: z.string().min(1).max(128),
  query: z.string().max(300).default(""),
  sort: CourseSortSchema.default("recommended"),
  language: z.string().max(20).optional(),
  max_duration_minutes: z.number().int().positive().optional(),
  limit: z.number().int().min(1).max(12).default(12),
  cursor: z.string().min(1).max(8192).optional(),
});
export const CourseLoginSchema = z.object({
  email: z.string().email().max(254),
});
export const CourseVerifySchema = CourseLoginSchema.extend({
  code: z.string().regex(/^\d{6}$/),
});
export const CourseLaunchSchema = z.object({
  recommendation_token: z.string().max(8192),
  request_id: z.string().min(8).max(128),
});
export const CourseInstructorSchema = z.object({
  name: z.string().min(1).max(200),
  image_url: z.string().default(""),
  role: z.string().default(""),
  company: z.string().default(""),
  bio: z.string().default(""),
});
export type CourseInstructorDto = z.infer<typeof CourseInstructorSchema>;
export const CourseCardSchema = z.object({
  course_type: z.enum(["internal", "external"]).default("external"),
  entity_id: z.string(),
  title: z.string(),
  provider_name: z.string(),
  instructor: z.string(),
  instructors: z.array(CourseInstructorSchema).max(20).default([]),
  provider_logo_url: z.string().default(""),
  description: z.string().default(""),
  image_url: z.string().default(""),
  lecture_count: z.number().nullable().optional(),
  skills: z.array(z.string()),
  language: z.string().nullable(),
  level: z.string(),
  duration_minutes: z.number().nullable(),
  rating: z.number().nullable(),
  rating_count: z.number().nullable(),
  access_mode: z.string(),
  is_demo: z.boolean(),
  reasons: z.array(z.string()),
  recommendation_token: z.string(),
  score: z.number(),
});
export type CourseCardDto = z.infer<typeof CourseCardSchema>;
export const CourseRecommendationsSchema = z.object({
  items: z.array(CourseCardSchema),
  sort: CourseSortSchema,
  sort_scope: z.enum(["job_candidates", "catalog"]),
  is_fallback: z.boolean().default(false),
  next_cursor: z.string().max(8192).nullable().default(null),
  intermediate_upskilling: z.boolean().default(false),
  trending_available: z.boolean(),
  retrieval_mode: z.string(),
  is_demo: z.boolean(),
});
export type CourseRecommendationsDto = z.infer<
  typeof CourseRecommendationsSchema
>;

export const CourseLaunchResponseSchema = z.object({ launch_url: z.string() });
export const CourseAuthResponseSchema = z.object({
  signed_in: z.boolean().default(false),
  masked_email: z.string().optional(),
});

export const CatalogSessionSchema = z.object({
  session_token: z.string().min(1),
  expires_in: z.number().int().positive(),
});
export const CandidateChallengeSchema = z.string().uuid();

export const CourseDetailRequestSchema = z.object({
  entity_id: z.string().regex(/^[a-f0-9]{64}$/),
  creator_handle: z.string().min(1).max(128),
  job_id: z.string().max(512).default(""),
});
export const CourseDetailSchema = CourseCardSchema.extend({
  description: z.string(),
  provider_url: z.string(),
  banner_image_url: z.string(),
  category: z.string(),
  levels: z.array(z.string()),
  level_label: z.string(),
  requirements: z.array(z.string()),
  learning_outcomes: z.array(z.string()),
  target_audience: z.array(z.string()),
  certification_covered: z.array(z.string()),
  curriculum: z.array(
    z.object({
      title: z.string(),
      description: z.string(),
      sections: z.array(
        z.object({
          title: z.string(),
          concepts: z.array(z.string()),
          labs: z.array(z.string()),
          tools: z.array(z.string()),
        }),
      ),
    }),
  ),
  provider_description: z.string().default(""),
  pricing_mode: z.enum(["subscription", "on_request", "fixed"]),
  price_amount: z.number().finite().nonnegative().nullable().default(null),
  original_price_amount: z
    .number()
    .finite()
    .nonnegative()
    .nullable()
    .default(null),
  currency: z.string().default(""),
  enquiry_token: z.string(),
}).superRefine((course, ctx) => {
  const fixed = course.pricing_mode === "fixed";
  if (
    fixed &&
    (course.price_amount === null || !/^[A-Z]{3}$/.test(course.currency))
  )
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Fixed prices require an amount and currency",
      path: ["price_amount"],
    });
  if (
    !fixed &&
    (course.price_amount !== null || course.original_price_amount !== null)
  )
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Amounts require fixed pricing",
      path: ["pricing_mode"],
    });
  if (
    course.original_price_amount !== null &&
    (course.price_amount === null ||
      course.original_price_amount < course.price_amount)
  )
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Original price cannot be below the current price",
      path: ["original_price_amount"],
    });
});
export type CourseDetailDto = z.infer<typeof CourseDetailSchema>;
export const CoursePhoneRequestSchema = z.object({
  phone: z.string().regex(/^\+[1-9]\d{7,14}$/),
});
export const CoursePhoneVerifySchema = CoursePhoneRequestSchema.extend({
  code: z.string().regex(/^\d{4,10}$/),
});
export const CourseEnquiryContactSchema = z
  .object({
    fullName: z.string().trim().min(2).max(120),
    email: z
      .string()
      .trim()
      .email()
      .max(254)
      .transform((value) => value.toLowerCase())
      .optional(),
    phone: CoursePhoneRequestSchema.shape.phone,
    experience: JobAlertExperienceSchema.optional(),
    experienceYears: z.number().min(0).max(60).optional(),
    consent: z.literal(true),
    consentVersion: z
      .enum([COURSE_ENQUIRY_CONSENT.legacy, COURSE_ENQUIRY_CONSENT.terms])
      .default(COURSE_ENQUIRY_CONSENT.legacy),
  })
  .refine(
    (value) =>
      (value.experience !== undefined) !==
      (value.experienceYears !== undefined),
    { message: "Select your experience", path: ["experience"] },
  );
export const CourseEnquirySchema = CourseEnquiryContactSchema.and(
  z.object({
    enquiryToken: z.string().min(1).max(8192),
  }),
);
export type CourseEnquiryDto = z.infer<typeof CourseEnquirySchema>;
export const ResolvedCourseEnquirySchema = z.object({
  courseId: z.string().regex(/^[a-f0-9]{64}$/),
  courseTitle: z.string().max(500),
  providerId: z.string().max(128),
  providerName: z.string().max(128),
  feedId: z.string().max(128),
  scopeId: z.string().max(128),
  creatorId: z.string().regex(/^[a-f0-9]{24}$/),
  sourceJobId: z.string().max(512),
});
export type ResolvedCourseEnquiryDto = z.infer<
  typeof ResolvedCourseEnquirySchema
>;
export const CourseEnquiryResponseSchema = z.object({
  leadId: z.string(),
  accepted: z.literal(true),
});

export const CoursePhoneRequestedSchema = z.object({ sent: z.literal(true) });
export const CoursePhoneVerifiedSchema = z.object({
  verified: z.literal(true),
});
export type CoursePhoneRequestedDto = z.infer<
  typeof CoursePhoneRequestedSchema
>;
export type CoursePhoneVerifiedDto = z.infer<typeof CoursePhoneVerifiedSchema>;
export type CourseEnquiryResponseDto = z.infer<
  typeof CourseEnquiryResponseSchema
>;

/** Only the current server-bound candidate may supply saved contact and verification state. */
export const CourseEnquiryProfileSchema = z.object({
  fullName: z.string(),
  email: z.string(),
  phone: z.string(),
  experienceYears: z.number().min(0).max(80).nullable(),
  experience: JobAlertExperienceSchema.nullable().optional(),
  phoneVerified: z.boolean(),
});
export type CourseEnquiryProfileDto = z.infer<
  typeof CourseEnquiryProfileSchema
>;

export const COURSE_ENQUIRY_SESSION = {
  cookie: "artha_course_enquiry_session",
  type: "course_enquiry",
  ttlSeconds: 30 * 24 * 60 * 60,
} as const;
